import { Permission, Workspace } from '@futrli-sdk/workspaces';
import queryString from 'query-string';
import { matchPath } from 'react-router-dom';

import { QUERY_PARAMS, ROUTE_PARAMS, ROUTES } from '../../app-core/constants';
import { getOrganisationDetailUrl } from '../../organisations/utils/routes-utils';
import { getPathMatch, interpolatePathParams, joinRouteMatchPath, joinRouteMatchUrl, relativePath } from '../../path';
import { ProductSlug } from '../../app-core';

export const getWorkspacesRoutePath = (): string => {
  return ROUTES.WORKSPACES_ROUTE;
};

export const matchWorkspacesRoutePath = (pathname: string, isExact = false) => {
  const routePath = getWorkspacesRoutePath();
  return getPathMatch(pathname, routePath, isExact);
};

export const getWorkspaceDetailRoutePath = (): string => {
  return joinRouteMatchPath(getWorkspacesRoutePath(), ROUTES.WORKSPACE_BY_ID_ROUTE);
};

export const matchWorkspaceDetailRoutePath = (pathname: string, isExact = false) => {
  const routePath = getWorkspaceDetailRoutePath();
  const routePathOrgsTab = joinRouteMatchPath(routePath, ROUTES.ORGANISATIONS_ROUTE);
  const routePathConsTab = joinRouteMatchPath(routePath, ROUTES.CONSOLIDATIONS_ROUTE);
  return (
    getPathMatch(pathname, routePath, isExact) ||
    getPathMatch(pathname, routePathOrgsTab, isExact) ||
    getPathMatch(pathname, routePathConsTab, isExact)
  );
};

export const getWorkspaceDetailSettingsRoutePath = (): string => {
  return joinRouteMatchPath(getWorkspaceDetailRoutePath(), ROUTES.WORKSPACE_BY_ID_SETTINGS_ROUTE);
};

/**
 * Gets the workspace detail URL.
 * @param {string} workspaceId
 * @returns URL
 */
export const getWorkspaceDetailUrl = (workspaceId: string): string =>
  interpolatePathParams(getWorkspaceDetailRoutePath(), {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

/**
 * Gets the workspace detail settings URL of a workspace.
 * @param {string} workspaceId
 * @return URL
 */
export const getWorkspaceDetailSettingsUrl = (workspaceId: string): string =>
  joinRouteMatchUrl(getWorkspaceDetailUrl(workspaceId), ROUTES.WORKSPACE_BY_ID_SETTINGS_ROUTE);

export const getRootUrl = (): string => joinRouteMatchUrl('', ROUTES.ROOT_ROUTE);

export const getOnboardingUrl = (): string => joinRouteMatchUrl('', ROUTES.ONBOARDING_ROUTE);

export const matchOnboardingPath = (pathname: string, isExact = false) => {
  const routePath = joinRouteMatchPath('', ROUTES.ONBOARDING_ROUTE);
  return getPathMatch(pathname, routePath, isExact);
};

export const getXeroAdditionalUrl = (): string =>
  joinRouteMatchUrl('', ROUTES.ONBOARDING_ROUTE, ROUTES.XERO_ADDITIONAL);

export const getSageIdAdditionalInfoUrl = (): string =>
  joinRouteMatchUrl('', ROUTES.ONBOARDING_ROUTE, ROUTES.ADDITIONAL_INFO);

export const matchSageIdAdditionalInfoPath = (pathname: string, isExact = false) => {
  const routePath = joinRouteMatchPath('', ROUTES.ONBOARDING_ROUTE, ROUTES.ADDITIONAL_INFO);
  return getPathMatch(pathname, routePath, isExact);
};

export const getOnboardingByProductUrl = (productSlug: ProductSlug): string => {
  const path = joinRouteMatchPath('', ROUTES.ONBOARDING_ROUTE, ROUTES.PRODUCT_SLUG_ROUTE);

  return interpolatePathParams(path, {
    [ROUTE_PARAMS.PRODUCT_SLUG]: productSlug
  });
};

/**
 * Gets orgs settings page /integrations url.
 *
 * @param {string} workspaceId
 * @param {string} organisationId
 * @param {string} productId
 * @returns {string}
 */
export const getIntegrationsOnboardingUrl = (
  workspaceId: string,
  organisationId: string,
  productId: string
): string => {
  const queryParams = queryString.stringify({
    [QUERY_PARAMS.WORKSPACE_ID]: workspaceId,
    [QUERY_PARAMS.ORG_ID]: organisationId,
    [QUERY_PARAMS.PRODUCT_ID]: productId
  });
  return joinRouteMatchPath('', getOnboardingUrl(), ROUTES.INTEGRATIONS_ROUTE, `?${queryParams}`);
};

/**
 * Checks is the pathname matches with the integrations job path.
 * @deprecated
 * @param {string} pathname
 * @return {boolean}
 */
export const matchIntegrationJobPath = (pathname: string): boolean => {
  const path = relativePath(ROUTES.INTEGRATIONS_ROUTE, ROUTES.INTEGRATION_META_ROUTE);

  return !!matchPath({ path, end: true }, pathname);
};

/**
 * Checks is the pathname matches with the integrations onboarding job path.
 * @deprecated
 * @param {string} pathname
 * @return {boolean}
 */
export const matchIntegrationOnboardingJobPath = (pathname: string): boolean => {
  const path = relativePath(ROUTES.INTEGRATIONS_ONBOARDING_ROUTE, ROUTES.INTEGRATION_META_ROUTE);

  return !!matchPath({ path, end: true }, pathname);
};

/**
 * Checks is the pathname matches with the integrations organisation settings path.
 * @param {string} pathname
 * @return {match|null}
 */
export const matchSettingsPath = (pathname: string): boolean => {
  const path = relativePath(
    ROUTES.WORKSPACES_ROUTE,
    ROUTES.WORKSPACE_BY_ID_ROUTE,
    ROUTES.WORKSPACE_BY_ID_SETTINGS_ROUTE,
    '*'
  );

  return !!matchPath({ path, end: true }, pathname);
};

/**
 * Gets the workspace detail url but destructures the given Workspace item first.
 *
 * @param {Workspace} workspace
 * @returns {string}
 */
export const changeWorkspace = ({ id }: Workspace): string => getWorkspaceDetailSettingsUrl(id);

/**
 * Matches a react router path against the current location.
 * Returns a boolean showing if the match is an exact match.
 *
 * @param {string} currentPath - The current path location
 * @param {string} matchingPath - The matching location
 * @param {boolean} isExact - Checking exact path or not.
 * @returns {boolean}
 */
export const checkPath = (currentPath: string, matchingPath: string, isExact = true): boolean => {
  const matchValue = matchPath(
    {
      path: isExact ? matchingPath : `${matchingPath}/*`,
      end: isExact
    },
    currentPath
  );

  return matchValue !== null;
};

/**
 * Checks permissions of a nav route against a permission slug.
 *
 * @param {Permission[]} permissions
 * @param {string} route
 * @returns {boolean}
 */
export const checkNavPermission = (permissions: Permission[] | null, route: string): boolean => {
  if (!permissions || (permissions && !permissions.length)) {
    return false;
  }
  const stripped = route.replace('/', '');

  return permissions.find((permission: Permission) => permission.slug === stripped) !== undefined;
};

/**
 * Newsfeed introduction url
 *
 * @param {string} workspaceId
 * @param {string} organisationId
 * @param {string} productSlug
 * @returns {string}
 */
export const getNewsfeedIntroUrl = (workspaceId: string, organisationId: string, productSlug: string): string =>
  interpolatePathParams(
    joinRouteMatchUrl(
      '',
      getOrganisationDetailUrl(workspaceId, organisationId),
      ROUTES.INTRODUCTION,
      ROUTES.PRODUCT_SLUG
    ),
    {
      [ROUTE_PARAMS.PRODUCT_SLUG]: productSlug
    }
  );

/**
 * Payment detail page url
 *
 * @param {string} workspaceId
 * @returns {string}
 */
export const getPaymentDetailsPageUrl = (workspaceId: string): string =>
  joinRouteMatchUrl('', getWorkspaceDetailUrl(workspaceId), ROUTES.PAYMENT_DETAILS);

export const matchPaymentDetailsPagePath = (pathname: string): boolean => {
  const path = [ROUTES.WORKSPACES_ROUTE, ROUTES.WORKSPACE_BY_ID_ROUTE, ROUTES.PAYMENT_DETAILS].join('');

  return !!matchPath({ path: `${path}/*`, end: true }, pathname);
};

/**
 * Checks whether the location passed as parameter matches with the Newsfeed page path.
 * @param {string} pathname
 * @returns {match|null}
 */
export const matchNewsfeedIntroPath = (pathname: string): ReturnType<typeof matchPath> => {
  const path = [
    ROUTES.WORKSPACES_ROUTE,
    ROUTES.WORKSPACE_BY_ID_ROUTE,
    ROUTES.ORGANISATIONS_ROUTE,
    ROUTES.ORGANISATION_BY_ID_ROUTE,
    ROUTES.INTRODUCTION,
    '/',
    ROUTES.PRODUCT_SLUG
  ].join('');

  return matchPath({ path: `${path}/*`, end: false }, pathname);
};
